import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import { Card, Btn, Pill, Modal, Empty, Notice, inputCls } from "@/dashboard/ui";
import { Megaphone, Plus, Pencil, Trash2, Copy, Check, X, Sparkles, AlertTriangle, Lightbulb, ShieldAlert } from "lucide-react";

const OBJECTIVES = {
  google: [["leads", "Leads"], ["website_traffic", "Website traffic"], ["calls", "Phone calls"], ["local_visits", "Local visits"], ["sales", "Sales"]],
  meta: [["awareness", "Awareness"], ["traffic", "Traffic"], ["engagement", "Engagement"], ["leads", "Leads"], ["sales", "Sales"]],
};
const PLACEMENTS = [["facebook_feed", "Facebook feed"], ["instagram_feed", "Instagram feed"], ["instagram_stories", "Instagram stories"], ["facebook_stories", "Facebook stories"], ["reels", "Reels"], ["audience_network", "Audience network"]];
const STATUS_TONE = { draft: "grey", pending_approval: "amber", approved: "green", rejected: "red" };
const STATUS_LABEL = { draft: "Draft", pending_approval: "Pending approval", approved: "Approved (ready — not live)", rejected: "Rejected" };

const EMPTY_GOOGLE = { channel: "google", name: "", objective: "leads", daily_budget: 10, currency: "GBP", start_date: "", end_date: "", locations: [], audience: "", keywords: [], negative_keywords: [], creative: { headlines: ["", "", ""], descriptions: ["", ""] } };
const EMPTY_META = { channel: "meta", name: "", objective: "leads", daily_budget: 10, currency: "GBP", start_date: "", end_date: "", locations: [], audience: "", placements: ["facebook_feed", "instagram_feed"], creative: { primary_text: "", headline: "", description: "" } };

function CampaignEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => JSON.parse(JSON.stringify(initial)));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setC = (k, v) => setForm((f) => ({ ...f, creative: { ...f.creative, [k]: v } }));
  const isGoogle = form.channel === "google";
  const listVal = (arr) => (arr || []).join("\n");
  const setList = (k, text) => set(k, text.split("\n").map((x) => x.trim()).filter(Boolean));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, start_date: form.start_date || null, end_date: form.end_date || null };
      if (form.id) await api.put(`/ads/campaigns/${form.id}`, payload);
      else await api.post("/ads/campaigns", payload);
      toast.success("Campaign draft saved — nothing is live or chargeable");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`${form.id ? "Edit" : "New"} ${isGoogle ? "Google Ads" : "Meta Ads"} campaign`} onClose={onClose} testid="ad-editor-modal">
      <div className="max-h-[72vh] space-y-3 overflow-y-auto pr-1">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block"><span className="mb-1 block text-xs font-semibold text-neutral-600">Campaign name *</span>
            <input className={inputCls} value={form.name} data-testid="ad-name" onChange={(e) => set("name", e.target.value)} /></label>
          <label className="block"><span className="mb-1 block text-xs font-semibold text-neutral-600">Objective</span>
            <select className={inputCls} value={form.objective} data-testid="ad-objective" onChange={(e) => set("objective", e.target.value)}>
              {OBJECTIVES[form.channel].map(([id, l]) => <option key={id} value={id}>{l}</option>)}
            </select></label>
          <label className="block"><span className="mb-1 block text-xs font-semibold text-neutral-600">Daily budget ({form.currency})</span>
            <input type="number" min="1" className={inputCls} value={form.daily_budget} data-testid="ad-budget" onChange={(e) => set("daily_budget", parseFloat(e.target.value) || 0)} /></label>
          <label className="block"><span className="mb-1 block text-xs font-semibold text-neutral-600">Locations (one per line)</span>
            <input className={inputCls} value={(form.locations || []).join(", ")} data-testid="ad-locations" onChange={(e) => set("locations", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></label>
          <label className="block"><span className="mb-1 block text-xs font-semibold text-neutral-600">Start date</span>
            <input type="date" className={inputCls} value={form.start_date || ""} data-testid="ad-start" onChange={(e) => set("start_date", e.target.value)} /></label>
          <label className="block"><span className="mb-1 block text-xs font-semibold text-neutral-600">End date</span>
            <input type="date" className={inputCls} value={form.end_date || ""} data-testid="ad-end" onChange={(e) => set("end_date", e.target.value)} /></label>
        </div>
        <label className="block"><span className="mb-1 block text-xs font-semibold text-neutral-600">Audience description</span>
          <input className={inputCls} value={form.audience || ""} data-testid="ad-audience" placeholder="e.g. Women 25-55 within 10 miles of Leeds" onChange={(e) => set("audience", e.target.value)} /></label>

        {isGoogle ? (
          <>
            <label className="block"><span className="mb-1 block text-xs font-semibold text-neutral-600">Keywords (one per line) *</span>
              <textarea rows={3} className={inputCls} value={listVal(form.keywords)} data-testid="ad-keywords" onChange={(e) => setList("keywords", e.target.value)} /></label>
            <label className="block"><span className="mb-1 block text-xs font-semibold text-neutral-600">Negative keywords (one per line)</span>
              <textarea rows={2} className={inputCls} value={listVal(form.negative_keywords)} data-testid="ad-negatives" onChange={(e) => setList("negative_keywords", e.target.value)} /></label>
            <div className="rounded-xl border border-neutral-200 p-3">
              <p className="mb-2 text-xs font-bold text-neutral-700">Responsive Search Ad — headlines (3–15, max 30 chars each)</p>
              <div className="space-y-1.5">
                {(form.creative.headlines || []).map((h, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input className={inputCls} value={h} maxLength={30} data-testid={`ad-headline-${i}`}
                      onChange={(e) => setC("headlines", form.creative.headlines.map((x, j) => (j === i ? e.target.value : x)))} />
                    <span className={`w-8 text-right text-[10px] ${h.length > 30 ? "text-red-600" : "text-neutral-400"}`}>{h.length}/30</span>
                  </div>
                ))}
              </div>
              <Btn type="button" variant="ghost" className="mt-2" data-testid="ad-add-headline" disabled={(form.creative.headlines || []).length >= 15}
                onClick={() => setC("headlines", [...(form.creative.headlines || []), ""])}>+ Headline</Btn>
              <p className="mb-2 mt-3 text-xs font-bold text-neutral-700">Descriptions (2–4, max 90 chars each)</p>
              <div className="space-y-1.5">
                {(form.creative.descriptions || []).map((d, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input className={inputCls} value={d} maxLength={90} data-testid={`ad-description-${i}`}
                      onChange={(e) => setC("descriptions", form.creative.descriptions.map((x, j) => (j === i ? e.target.value : x)))} />
                    <span className={`w-8 text-right text-[10px] ${d.length > 90 ? "text-red-600" : "text-neutral-400"}`}>{d.length}/90</span>
                  </div>
                ))}
              </div>
              <Btn type="button" variant="ghost" className="mt-2" data-testid="ad-add-description" disabled={(form.creative.descriptions || []).length >= 4}
                onClick={() => setC("descriptions", [...(form.creative.descriptions || []), ""])}>+ Description</Btn>
            </div>
          </>
        ) : (
          <div className="space-y-2.5 rounded-xl border border-neutral-200 p-3">
            <label className="block"><span className="mb-1 block text-xs font-semibold text-neutral-600">Primary text * (max 125)</span>
              <textarea rows={3} className={inputCls} value={form.creative.primary_text || ""} maxLength={125} data-testid="ad-primary-text" onChange={(e) => setC("primary_text", e.target.value)} /></label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block"><span className="mb-1 block text-xs font-semibold text-neutral-600">Headline * (max 40)</span>
                <input className={inputCls} value={form.creative.headline || ""} maxLength={40} data-testid="ad-meta-headline" onChange={(e) => setC("headline", e.target.value)} /></label>
              <label className="block"><span className="mb-1 block text-xs font-semibold text-neutral-600">Description (max 30)</span>
                <input className={inputCls} value={form.creative.description || ""} maxLength={30} data-testid="ad-meta-description" onChange={(e) => setC("description", e.target.value)} /></label>
            </div>
            <div>
              <span className="mb-1 block text-xs font-semibold text-neutral-600">Placements</span>
              <div className="flex flex-wrap gap-1.5" data-testid="ad-placements">
                {PLACEMENTS.map(([id, l]) => (
                  <button key={id} type="button" data-testid={`ad-placement-${id}`}
                    onClick={() => set("placements", (form.placements || []).includes(id) ? form.placements.filter((x) => x !== id) : [...(form.placements || []), id])}
                    className={`rounded-full border px-3 py-1 text-xs font-medium ${(form.placements || []).includes(id) ? "border-[#111111] bg-[#111111] text-white" : "border-neutral-300 text-neutral-600"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn data-testid="ad-save" disabled={saving || !form.name.trim()} onClick={save}>{saving ? "Saving…" : "Save draft"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

export default function AdvertisingPanel() {
  const [channel, setChannel] = useState("google");
  const [campaigns, setCampaigns] = useState(null);
  const [connections, setConnections] = useState(null);
  const [editing, setEditing] = useState(null);
  const [gen, setGen] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [metrics, setMetrics] = useState(null);

  const load = () => {
    api.get("/ads/campaigns").then((r) => setCampaigns(r.data.campaigns)).catch((e) => toast.error(formatApiError(e)));
    api.get("/ads/connections").then((r) => setConnections(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = (campaigns || []).filter((c) => c.channel === channel);

  const generate = async () => {
    setGenBusy(true);
    try {
      await api.post("/ads/generate", { channel: gen.channel, objective: gen.objective, daily_budget: parseFloat(gen.daily_budget) || 10, currency: "GBP", locations: gen.locations ? gen.locations.split(",").map((x) => x.trim()).filter(Boolean) : [], extra: gen.extra });
      toast.success("AI draft created — review and edit before approving");
      setGen(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenBusy(false);
    }
  };

  const review = async (c, action) => {
    try {
      const { data } = await api.post(`/ads/campaigns/${c.id}/review`, { action });
      toast.success(data.note);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const suggest = async (c) => {
    try {
      const { data } = await api.post(`/ads/campaigns/${c.id}/suggest`);
      setSuggestions({ c, ...data });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const showMetrics = async (c) => {
    const { data } = await api.get(`/ads/campaigns/${c.id}/metrics`);
    setMetrics({ c, ...data });
  };

  const conn = connections?.[channel === "google" ? "google_ads" : "meta_ads"];

  return (
    <div className="space-y-5" data-testid="advertising-panel">
      <Notice testid="ads-safety-note" title="Planning only — nothing can launch or spend"
        body="Drafts, approvals and AI suggestions live here. Launching ads requires the official ad-platform connection (not set up), and there is no launch button anywhere in the app. Performance metrics appear only from authorised API data." />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex rounded-full border border-neutral-300 p-0.5" data-testid="ads-channel-switch">
          {[["google", "Google Ads"], ["meta", "Meta Ads"]].map(([id, l]) => (
            <button key={id} data-testid={`ads-channel-${id}`} onClick={() => setChannel(id)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${channel === id ? "bg-[#111111] text-white" : "text-neutral-500"}`}>{l}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <Btn variant="ghost" data-testid="ads-ai-generate" onClick={() => setGen({ channel, objective: "leads", daily_budget: 10, locations: "", extra: "" })}>
            <Sparkles size={14} className="mr-1 inline" /> AI draft
          </Btn>
          <Btn data-testid="ads-new-campaign" onClick={() => setEditing(JSON.parse(JSON.stringify(channel === "google" ? EMPTY_GOOGLE : EMPTY_META)))}>
            <Plus size={15} /> New campaign
          </Btn>
        </div>
      </div>

      {conn && (
        <Card className="p-5" data-testid="ads-connection-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-bold text-[#111111]">{channel === "google" ? "Google Ads" : "Meta Ads"} connection</p>
            <Pill tone="grey" data-testid="ads-connection-state">Needs setup</Pill>
          </div>
          <p className="mt-2 text-xs font-semibold text-neutral-600">Required before any real campaign or metrics:</p>
          <ul className="mt-1 list-inside list-disc space-y-0.5 text-xs text-neutral-500">
            {conn.requirements.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </Card>
      )}

      {campaigns === null ? <p className="py-8 text-center text-sm text-neutral-400">Loading…</p> : shown.length === 0 ? (
        <Empty testid="ads-empty" icon={Megaphone} title={`No ${channel === "google" ? "Google" : "Meta"} campaigns yet`}
          body="Draft a campaign manually or let AI write one from your marketing profile. Everything stays in draft — approval never launches anything." />
      ) : (
        <div className="space-y-3">
          {shown.map((c) => (
            <Card key={c.id} className="p-5" data-testid={`ad-card-${c.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-bold text-[#111111]">{c.name}</p>
                    <Pill tone={STATUS_TONE[c.status]} data-testid={`ad-status-${c.id}`}>{STATUS_LABEL[c.status]}</Pill>
                    {c.ai_generated && <Pill tone="outline">AI draft</Pill>}
                  </div>
                  <p className="mt-1 text-xs text-neutral-500">
                    {OBJECTIVES[c.channel].find(([id]) => id === c.objective)?.[1]} · {c.currency} {c.daily_budget}/day
                    {c.start_date ? ` · ${c.start_date} → ${c.end_date || "no end"}` : ""}{(c.locations || []).length ? ` · ${c.locations.join(", ")}` : ""}
                  </p>
                  {(c.warnings || []).length > 0 && (
                    <div className="mt-2 space-y-0.5" data-testid={`ad-warnings-${c.id}`}>
                      {c.warnings.map((w, i) => <p key={i} className="flex items-center gap-1 text-[11px] text-amber-700"><AlertTriangle size={11} /> {w}</p>)}
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 gap-1">
                  <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100" title="Edit" data-testid={`ad-edit-${c.id}`} onClick={() => setEditing(JSON.parse(JSON.stringify(c)))}><Pencil size={14} /></button>
                  <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100" title="Duplicate" data-testid={`ad-duplicate-${c.id}`}
                    onClick={async () => { await api.post(`/ads/campaigns/${c.id}/duplicate`); toast.success("Duplicated"); load(); }}><Copy size={14} /></button>
                  <button className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-600" title="Delete" data-testid={`ad-delete-${c.id}`}
                    onClick={async () => { if (window.confirm(`Delete campaign "${c.name}"?`)) { await api.delete(`/ads/campaigns/${c.id}`); load(); } }}><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {c.status !== "approved" && (
                  <Btn variant="ghost" data-testid={`ad-approve-${c.id}`} onClick={() => review(c, "approve")}>
                    <Check size={13} className="mr-1 inline" /> Approve
                  </Btn>
                )}
                {c.status !== "rejected" && (
                  <Btn variant="ghost" data-testid={`ad-reject-${c.id}`} onClick={() => review(c, "reject")}>
                    <X size={13} className="mr-1 inline" /> Reject
                  </Btn>
                )}
                <Btn variant="ghost" data-testid={`ad-suggest-${c.id}`} onClick={() => suggest(c)}>
                  <Lightbulb size={13} className="mr-1 inline" /> AI suggestions
                </Btn>
                <Btn variant="ghost" data-testid={`ad-metrics-${c.id}`} onClick={() => showMetrics(c)}>Metrics</Btn>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && <CampaignEditor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      {gen && (
        <Modal title={`AI ${gen.channel === "google" ? "Google" : "Meta"} Ads draft`} onClose={() => setGen(null)} testid="ad-generate-modal">
          <div className="space-y-3">
            <label className="block"><span className="mb-1 block text-xs font-semibold text-neutral-600">Objective</span>
              <select className={inputCls} value={gen.objective} data-testid="gen-objective" onChange={(e) => setGen((g) => ({ ...g, objective: e.target.value }))}>
                {OBJECTIVES[gen.channel].map(([id, l]) => <option key={id} value={id}>{l}</option>)}
              </select></label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block"><span className="mb-1 block text-xs font-semibold text-neutral-600">Daily budget (£)</span>
                <input type="number" min="1" className={inputCls} value={gen.daily_budget} data-testid="gen-budget" onChange={(e) => setGen((g) => ({ ...g, daily_budget: e.target.value }))} /></label>
              <label className="block"><span className="mb-1 block text-xs font-semibold text-neutral-600">Locations</span>
                <input className={inputCls} value={gen.locations} data-testid="gen-locations" placeholder="Leeds, Bradford" onChange={(e) => setGen((g) => ({ ...g, locations: e.target.value }))} /></label>
            </div>
            <label className="block"><span className="mb-1 block text-xs font-semibold text-neutral-600">Notes for the AI</span>
              <input className={inputCls} value={gen.extra} data-testid="gen-extra" placeholder="e.g. promote midweek appointments" onChange={(e) => setGen((g) => ({ ...g, extra: e.target.value }))} /></label>
            <p className="text-[11px] text-neutral-400">Uses your marketing profile. Copy respects platform character limits and never invents offers or claims.</p>
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setGen(null)}>Cancel</Btn>
              <Btn data-testid="gen-submit" disabled={genBusy} onClick={generate}>{genBusy ? "Generating…" : "Generate draft"}</Btn>
            </div>
          </div>
        </Modal>
      )}

      {suggestions && (
        <Modal title={`AI suggestions — ${suggestions.c.name}`} onClose={() => setSuggestions(null)} testid="ad-suggestions-modal">
          <div className="space-y-2.5" data-testid="ad-suggestions-list">
            {suggestions.suggestions.map((s, i) => (
              <div key={i} className="rounded-xl border border-neutral-200 p-3.5">
                <p className="text-xs font-bold text-[#111111]">{s.area}</p>
                <p className="mt-0.5 text-xs text-neutral-700">{s.suggestion}</p>
                <p className="mt-0.5 text-[11px] text-neutral-400">{s.why}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-neutral-400">{suggestions.note}</p>
        </Modal>
      )}

      {metrics && (
        <Modal title={`Metrics — ${metrics.c.name}`} onClose={() => setMetrics(null)} testid="ad-metrics-modal">
          <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-center" data-testid="ad-metrics-unavailable">
            <ShieldAlert size={20} className="mx-auto text-neutral-400" />
            <p className="mt-2 text-sm font-semibold text-neutral-700">No metrics available</p>
            <p className="mt-1 text-xs text-neutral-500">{metrics.reason}</p>
          </div>
        </Modal>
      )}
    </div>
  );
}
